import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Button, FormikField as Field, H1, Input, Label, ListRow, Text } from '@betterment/samba-react';
import { SambaAutoLink, WorkflowLayout } from '@betterment/shared-components';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';
import { Form, Formik } from 'formik';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

import { NameAndImagePurposeCreationOptionsDocument } from './graphql/query.purposeCreationOption.generated';
import { nameAndImage } from './sharedSchemas';

import { PurposeCreationIntent } from '../../graphql/schema';
import Footer from '../../Shared/components-v2/footer/Footer';

type FormValues = Yup.InferType<typeof nameAndImage>;

type Props = {
  handleSubmit: (values: FormValues) => void;
  intent: PurposeCreationIntent;
};

export const NameAndImage: React.FC<Props> = ({ handleSubmit, intent }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const { data } = useGraphQLQuery(NameAndImagePurposeCreationOptionsDocument, {
    variables: { intent },
  });

  if (!data) {
    return '';
  }

  const { defaultName, purposeAsset } = data.purposeCreationOptions[0];

  const initialValues = {
    name: defaultName,
    imageType: data.purposeAssets.find(asset => asset.icon.id === purposeAsset.icon.id)?.legacyImageKey,
  };

  return (
    <WorkflowLayout pageTitle={null}>
      <WorkflowLayout.Header
        workflowTitle="Choose a name and image for this goal"
        onClose={() => (returnTo ? navigate(returnTo) : navigate('/'))}
      />
      <WorkflowLayout.Content>
        <H1>Choose a name and image for this goal.</H1>
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={nameAndImage}
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize={true}
        >
          <Form>
            <Box css={{ display: 'grid', gap: 'space.200', maxWidth: '400px' }}>
              <Field name="name">
                <Field.Label>Goal name</Field.Label>
                <Field.Control>
                  <Input />
                </Field.Control>
                <Field.Description>You can change this name anytime.</Field.Description>
                <Field.ErrorMessage />
              </Field>

              <Label>Goal image</Label>
              <SambaAutoLink to="/image_selection">
                <ListRow css={{ p: 0 }}>
                  <ListRow.LeadingAsset css={{ w: 'size.450', h: 'size.450' }}>
                    <ThemedImage themedAsset={purposeAsset.icon} alt={defaultName} />
                  </ListRow.LeadingAsset>
                  <ListRow.LeadingContent>
                    <Text css={{ verticalAlign: 'center' }}>Change or upload</Text>
                  </ListRow.LeadingContent>
                </ListRow>
              </SambaAutoLink>
              <Button type="submit" variant="solidPrimary" css={{ width: 'fit-content' }}>
                Continue
              </Button>
            </Box>
          </Form>
        </Formik>
      </WorkflowLayout.Content>
      <WorkflowLayout.Footer>
        <Footer />
      </WorkflowLayout.Footer>
    </WorkflowLayout>
  );
};
